import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Form, Input, Modal, Pagination, Select, Spin, Typography, message } from 'antd';
import api from '../../services/api';
import { PdfFieldMapping } from '../../types';

type Answer = string | number | boolean | null;
type Answers = Record<string, Answer>;

export default function DocumentAnswersModal({ open, onClose, onSaved, estateId, documentUid, title }: {
  open: boolean; onClose: () => void; onSaved: () => void; estateId: string; documentUid: string; title: string;
}) {
  const [form] = Form.useForm();
  const [fields, setFields] = useState<PdfFieldMapping[]>([]);
  const [stored, setStored] = useState<Answers>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!open || !estateId || !documentUid) return;
    setLoading(true);
    Promise.all([
      api.get(`/probatedocs/${documentUid}/field-mappings`),
      api.get(`/estates/${estateId}/documents/${documentUid}/answers`),
    ]).then(([mappingResponse, answerResponse]) => {
      const manual = (mappingResponse.data as PdfFieldMapping[]).filter(field => field.relatedEntity === 'documentanswers');
      const values = answerResponse.data.values || {};
      setFields(manual);
      setStored(values);
      form.setFieldsValue(values);
    }).catch(() => message.error('Could not load document answers')).finally(() => setLoading(false));
  }, [open, estateId, documentUid, form]);

  const filtered = useMemo(() => fields.filter(field =>
    `${field.pdfFieldName} ${field.surroundingText || ''} ${field.pageNumber}`.toLowerCase().includes(search.toLowerCase())), [fields, search]);
  const visible = filtered.slice((page - 1) * 20, page * 20);
  const answered = fields.filter(field => {
    const value = stored[field.pdfFieldName];
    return value !== undefined && value !== null && value !== '' && value !== false;
  }).length;

  const save = async () => {
    setSaving(true);
    try {
      const values = { ...stored, ...form.getFieldsValue(true) } as Answers;
      await api.put(`/estates/${estateId}/documents/${documentUid}/answers`, { values });
      setStored(values);
      message.success('Document answers saved');
      onSaved();
      onClose();
    } catch { message.error('Could not save document answers'); }
    finally { setSaving(false); }
  };

  return <Modal open={open} title={`Additional answers: ${title}`} onCancel={onClose} width={780}
    footer={<Button type="primary" loading={saving} onClick={save}>Save answers</Button>}>
    {loading ? <Spin /> : <>
      <Alert type="info" showIcon message={`${answered} of ${fields.length} document-specific fields answered`}
        description="Shared estate facts fill automatically. Complete only the remaining questions that apply. Leave signature lines for the actual signer."
        style={{ marginBottom: 16 }} />
      <Input.Search placeholder="Find a PDF field or nearby label" value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} style={{ marginBottom: 16 }} />
      <Form form={form} layout="vertical">
        {visible.map(field => {
          const label = field.surroundingText?.trim() || field.pdfFieldName;
          const checkbox = /checkbox/i.test(field.type);
          const pendingAction = field.pdfFieldName === 'Cause(s) of Pending Action';
          const administrationType = field.pdfFieldName === 'Combo Box0' && title.includes('DISTRIBUTEE');
          return <Form.Item key={field.id} name={field.pdfFieldName} valuePropName={checkbox ? 'checked' : 'value'}
            label={<><Typography.Text strong>Page {field.pageNumber}: {label}</Typography.Text><br /><Typography.Text type="secondary">{field.pdfFieldName}</Typography.Text></>}>
            {checkbox ? <Checkbox>Checked</Checkbox> : pendingAction ? <Select allowClear options={['Yes', 'No'].map(value => ({ value, label: value }))} />
              : administrationType ? <Select allowClear options={['LETTERS OF ADMINISTRATION', 'LETTERS OF ADMINISTRATION D.B.N.', 'VOLUNTARY ADMINISTRATION'].map(value => ({ value, label: value }))} />
              : <Input />}
          </Form.Item>;
        })}
      </Form>
      <Pagination current={page} pageSize={20} total={filtered.length} showSizeChanger={false} onChange={setPage} />
    </>}
  </Modal>;
}
